"use client";

import { useState } from "react";
import { Button } from "@ui/components/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from "@ui/components/dropdown-menu";
import { ClipboardCopyIcon, DownloadIcon, FileImageIcon, FileSpreadsheetIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import {
  exportChartAsImage,
  copyChartImageToClipboard,
  copyRichTextToClipboard,
  convertToCSV,
  exportCategoryDataAsCSV,
  exportLocationDataAsCSV,
  exportTimelineDataAsCSV
} from "@repo/utils";
import { cn } from "@ui/lib";

interface ExportButtonProps {
  // For CSV export
  data?: any[];
  csvFilename?: string;
  csvType?: 'categories' | 'locations' | 'timeline' | 'custom';
  componentType?: string;
  propertyName?: string;
  locationLevel?: string;
  
  // For filtered CSV export
  filterInfo?: {
    timeRange?: string;
    pagePath?: string;
    aiModel?: string;
    country?: string;
    region?: string;
    city?: string;
  };
  
  // For image export
  chartElementId?: string;

  /**
   * Optional rich-text (HTML) representation for "Copy to clipboard". When
   * provided, copy writes this HTML (so it pastes as formatted content, e.g. a
   * colored data table) instead of rasterizing the chart element to an image.
   * Use for charts that don't rasterize cleanly (e.g. donut legends).
   */
  copyHtml?: string;


  // Component props
  size?: 'sm' | 'md' | 'lg' | 'icon';
  variant?: 'outline' | 'ghost' | 'primary' | 'secondary' | 'error' | 'light' | 'link';
  className?: string;
  disabled?: boolean;
}

export function ExportButton({
  data,
  csvFilename,
  csvType = 'custom',
  componentType = 'data',
  propertyName,
  locationLevel = 'country',
  filterInfo,
  chartElementId,
  copyHtml,
  size = 'sm',
  variant = 'outline',
  className,
  disabled = false
}: ExportButtonProps) {
  const t = useTranslations("shared.export");
  const [isExporting, setIsExporting] = useState(false);

  const handleCSVExport = async () => {
    if (!data || data.length === 0) {
      console.warn('No data available for CSV export');
      return;
    }

    setIsExporting(true);
    
    try {
      const baseFilename = propertyName 
        ? `${propertyName}-${componentType}-${new Date().toISOString().split('T')[0]}`
        : `${componentType}-${new Date().toISOString().split('T')[0]}`;
      const filename = csvFilename || baseFilename;
      
      switch (csvType) {
        case 'categories':
          exportCategoryDataAsCSV(data, propertyName, componentType);
          break;
        case 'locations':
          exportLocationDataAsCSV(data, propertyName, componentType, locationLevel);
          break;
        case 'timeline':
          exportTimelineDataAsCSV(data, propertyName, componentType, filterInfo);
          break;
        default:
          convertToCSV(data, filename);
      }
    } catch (error) {
      console.error('CSV export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImageExport = async (format: 'png' | 'jpg' = 'png') => {
    if (!chartElementId) {
      console.warn('No chart element ID provided for image export');
      return;
    }

    setIsExporting(true);
    
    try {
      await exportChartAsImage(chartElementId, propertyName, componentType, format);
    } catch (error) {
      console.error('Image export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Build a tab/newline-delimited text representation of `data` for the
  // clipboard text fallback (used when an app can't accept the image, or when
  // the browser doesn't support image clipboard writes).
  const buildTextFallback = (): string => {
    if (!data || data.length === 0) return componentType;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((h) => `${row[h] ?? ""}`).join("\t"),
    );
    return [headers.join("\t"), ...rows].join("\n");
  };

  const handleCopyToClipboard = async () => {
    if (!chartElementId && !copyHtml) return;
    setIsExporting(true);
    try {
      if (copyHtml) {
        // Rich-text path: paste a formatted (colored) data table. Preferred for
        // charts whose DOM doesn't rasterize cleanly (donut legends).
        await copyRichTextToClipboard(copyHtml, buildTextFallback());
      } else {
        await copyChartImageToClipboard(chartElementId!, {
          textFallback: buildTextFallback(),
        });
      }
      toast.success(t("copied"));
    } catch (error) {
      console.error("Copy to clipboard failed:", error);
      toast.error(t("copyFailed"));
    } finally {
      setIsExporting(false);
    }
  };

  const canExportCSV = data && data.length > 0;
  const canExportImage = !!chartElementId;
  const canCopy = !!chartElementId || !!copyHtml;

  if (!canExportCSV && !canExportImage && !canCopy) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={disabled || isExporting}
          className={cn("gap-2", className)}
        >
          <DownloadIcon className="h-4 w-4" />
          {isExporting ? t("exporting") : t("export")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canExportCSV && (
          <>
            <DropdownMenuItem onClick={handleCSVExport} disabled={isExporting}>
              <FileSpreadsheetIcon className="h-4 w-4 mr-2" />
              {t("exportCSV")}
            </DropdownMenuItem>
            {(canCopy || canExportImage) && <DropdownMenuSeparator />}
          </>
        )}
        {canCopy && (
          <DropdownMenuItem onClick={handleCopyToClipboard} disabled={isExporting}>
            <ClipboardCopyIcon className="h-4 w-4 mr-2" />
            {t("copyClipboard")}
          </DropdownMenuItem>
        )}
        {canExportImage && (
          <>
            {canCopy && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={() => handleImageExport('png')} disabled={isExporting}>
              <FileImageIcon className="h-4 w-4 mr-2" />
              {t("exportPNG")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleImageExport('jpg')} disabled={isExporting}>
              <FileImageIcon className="h-4 w-4 mr-2" />
              {t("exportJPG")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 